// src/components/chat-container.ts
import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import './chat-list'
import './chat-view'

import { Message, Character } from '../store'

export interface ChatItem {
    id: string
    name: string
    personalityId: string
    lastMessage: string
    timestamp: number
}

@customElement('chat-container')
export class ChatContainer extends LitElement {
    @property({ type: Array }) chats: ChatItem[] = []
    @property({ type: String }) selectedChatId = ''
    @property({ type: Array }) messages: Message[] = []
    @property({ type: Boolean }) showChatList = true
    @property({ type: Boolean }) isMobile = false
    @property({ type: Array }) characters: Character[] = []
    @property({ type: String }) activeCharacter: string | null = null

    static styles = css`
        :host {
            display: block;
            height: 100%;
        }

        .with-sidebar {
            display: flex;
            flex-wrap: wrap;
            height: 100%;
            background-color: var(--bg-primary);
        }

        /* The sidebar */
        .sidebar {
            flex-basis: 0;
            flex-grow: 0;
            height: 100%;
            overflow: hidden;
            background-color: var(--bg-primary);
            max-width: 30rem;
            width: 0;
            visibility: hidden;
            opacity: 0;
            transition:
                flex-basis var(--duration-normal) var(--ease-in-out),
                width var(--duration-normal) var(--ease-in-out),
                visibility var(--duration-normal) var(--ease-in-out),
                opacity var(--duration-normal) var(--ease-in-out);
        }

        .sidebar.visible {
            flex-basis: 30rem;
            flex-grow: 1;
            width: 30rem;
            visibility: visible;
            opacity: 1;
        }

        /* The not-sidebar (main content) */
        .not-sidebar {
            flex-basis: 0;
            flex-grow: 999;
            min-inline-size: 50%;
            height: 100%;
            overflow: hidden;
            background-color: var(--color-background);
            transition: flex-grow var(--duration-normal) var(--ease-in-out);
        }

        /* Overlay (only used on mobile) */
        .overlay {
            display: none;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 4;
            animation: fade-in var(--duration-normal) var(--ease-in-out);
        }

        /* Mobile-specific adjustments */
        @media (max-width: 60rem) {
            .with-sidebar {
                gap: 0;
            }

            .sidebar {
                position: fixed;
                top: 0;
                left: 0;
                bottom: 0;
                width: 100%;
                max-width: 100%;
                height: auto;
                z-index: 5;
                visibility: visible;
                opacity: 1;
                transform: translateX(-100%);
                transition: transform var(--duration-normal) var(--ease-in-out);
            }

            .sidebar.visible {
                width: 100%;
                transform: translateX(0);
            }

            .overlay.visible {
                display: block;
            }
        }
    `

    private _handleChatSelected = (e: CustomEvent) => {
        e.stopPropagation()

        this.dispatchEvent(
            new CustomEvent('chat-selected', {
                detail: e.detail,
                bubbles: true,
                composed: true,
            }),
        )

        // Close sidebar on mobile after selection
        if (this.isMobile) {
            this._closeMobileSidebar()
        }
    }

    private _closeMobileSidebar = (): void => {
        this.dispatchEvent(
            new CustomEvent('toggle-chat-list', {
                bubbles: true,
                composed: true,
            }),
        )
    }

    private _handleNewChatRequested = (e: CustomEvent) => {
        e.stopPropagation()
        this.dispatchEvent(
            new CustomEvent('new-chat-requested', {
                bubbles: true,
                composed: true,
            }),
        )
    }

    render() {
        // Find active chat for personalityId
        const activeChat = this.chats.find(chat => chat.id === this.selectedChatId)
        const personalityId = activeChat?.personalityId || ''
        const personalityName = activeChat?.name || ''

        return html`
            ${this.isMobile ? html`` : ''}

            <div
                class="overlay ${this.showChatList ? 'visible' : ''}"
                @click=${this._closeMobileSidebar}
            ></div>

            <div class="with-sidebar">
                <div class="sidebar ${this.showChatList ? 'visible' : ''}">
                    <chat-list
                        .chats=${this.chats}
                        .selectedChatId=${this.selectedChatId}
                        @new-chat-requested=${this._handleNewChatRequested}
                        @chat-selected=${this._handleChatSelected}
                    ></chat-list>
                </div>

                <div class="not-sidebar">
                    <chat-view
                        .messages=${this.messages}
                        .personalityId=${personalityId}
                        .personalityName=${personalityName}
                    ></chat-view>
                </div>
            </div>
        `
    }
}
